import { RESERVED_ROLE_SLUGS } from '../constants.js';

/**
 * SB-8853 — group session participants by role SLUG (not core role) so custom
 * roles ("panelist") get their own section with the organizer's label.
 * Reserved trio first in canonical order, then custom slugs in payload order.
 *
 * @param {Array<{role_slug?: string, role?: string, role_label?: string|null, role_label_plural?: string|null}>} speakers
 * @returns {Array<{slug: string, label: string|null, speakers: Array}>} non-empty groups
 */
export const groupSpeakersBySlug = speakers => {
  const groupsBySlug = new Map(RESERVED_ROLE_SLUGS.map(slug => [slug, { slug, label: null, speakers: [] }]));
  for (const speaker of speakers || []) {
    const slug = speaker.role_slug || speaker.role || 'speaker';
    if (!groupsBySlug.has(slug)) {
      groupsBySlug.set(slug, { slug, label: null, speakers: [] });
    }
    const group = groupsBySlug.get(slug);
    if (!group.label && (speaker.role_label_plural || speaker.role_label)) {
      group.label = speaker.role_label_plural || speaker.role_label;
    }
    group.speakers.push(speaker);
  }
  return [...groupsBySlug.values()].filter(group => group.speakers.length > 0);
};

const RESERVED_HEADING_KEYS = {
  speaker: 'role_speakers',
  moderator: 'role_moderators',
  chairperson: 'role_chairpersons',
};

// Stock seeded names for the reserved trio (singular + plural). When the
// organizer never renamed the role, the payload still carries these English
// labels on v2 participant rows — that is not an intentional label, so the
// widget's translated built-in must win or a French page shows "Speakers"
// on subsession participants while legacy rows next to them translate.
const STOCK_TRIO_LABELS = {
  speaker: new Set(['speaker', 'speakers']),
  moderator: new Set(['moderator', 'moderators']),
  chairperson: new Set(['chairperson', 'chairpersons']),
};

/**
 * Section heading for a group: organizer label when it is a real rename
 * (custom roles always; trio roles only when renamed from the stock name),
 * the widget's translated built-in for the reserved trio otherwise,
 * capitalized slug as last resort.
 * @param {(key: string) => string} t
 * @param {{slug: string, label: string|null}} group
 */
export const roleGroupHeading = (t, group) => {
  const reservedKey = RESERVED_HEADING_KEYS[group.slug];
  if (group.label) {
    const stock = STOCK_TRIO_LABELS[group.slug];
    const isStockName = stock && stock.has(group.label.trim().toLowerCase());
    if (!isStockName) return group.label;
  }
  if (reservedKey) return t(reservedKey);
  return group.slug.charAt(0).toUpperCase() + group.slug.slice(1) + 's';
};

const RESERVED_SINGULAR_KEYS = {
  speaker: 'role_speaker',
  moderator: 'role_moderator',
  chairperson: 'role_chairperson',
};

/**
 * SB-9286 — one entry of a Speakers card's "Roles:" list. The payload gives a
 * display string: the organizer label, or the raw core slug ("speaker") when
 * the event has no label for the role. A slug or the stock trio name gets the
 * translated built-in, same rule as `roleGroupHeading`; a real rename or a
 * custom role label is shown as-is.
 * @param {(key: string) => string} t
 * @param {string} role
 */
export const roleChipLabel = (t, role) => {
  if (typeof role !== 'string') return role;
  const normalized = role.trim().toLowerCase();
  const slug = Object.keys(STOCK_TRIO_LABELS).find(key => STOCK_TRIO_LABELS[key].has(normalized));
  return slug ? t(RESERVED_SINGULAR_KEYS[slug]) : role;
};
